import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"
import {
    Box,
    SortDirection,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Button,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { MouseEvent, ReactElement, memo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import AppTableCell from "./appTableCell"
import AppTableContentRow from "./appTableContentRow"
import { LCTOnlyDatasets } from "../../constants/enums"
import {
    ASC,
    DESC,
    DRAFT_AMENDMENT,
    DRAFT_DEVICE,
} from "../../constants/generalConstants"
import {
    TT_COMPLIANCE_STATUS,
    TT_DATE_PUBLISHED,
    TT_MANUFACTURER,
    TT_MAX_DEMAND,
    TT_MODEL,
    TT_PHASE,
    TT_SYS_REFERENCE,
    TT_TYPE,
    TT_VOLTAGE,
} from "../../constants/tooltips"
import {
    SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK,
    SINGLE_DEMAND_DEVICE_NAV_LINK,
} from "../../router/routes"
import {
    IDemDatabaseSearchResult,
    fieldMapper,
} from "../../services/middleware/mappers/searchDemandDevicesMapper"
import AlertFlag from "../alert/alertFlag"
import CopyTypography from "../dataDisplay/typography/copyTypography"
import ZeroSearchResultsMsg from "../fallback/zeroSearchResultsMsg"
import AppTooltip from "../helper/appTooltip"
import AppSkeleton from "../loading/skeletons/appSkeleton"
import StatusChip from "../pills/status/statusChip"
import AppPaper from "../surfaces/appPaper"

interface IHeading {
    title: string
    tooltip: string
    id: string
    center: boolean
    enableSort: boolean
}

/**
 * Defines headings for table
 * @param dataset - lct dataset
 * @returns - array of headings and config for each to be injected
 * into search results table
 */
const headings = (dataset: LCTOnlyDatasets): IHeading[] => [
    {
        title: "System Reference",
        id: fieldMapper.deviceReference[dataset],
        tooltip: TT_SYS_REFERENCE,
        center: false,
        enableSort: true,
    },
    {
        title: "Compliance Status",
        id: "",
        tooltip: TT_COMPLIANCE_STATUS,
        center: true,
        enableSort: false,
    },
    {
        title: "Date Published",
        id: fieldMapper.publishedDate[dataset],
        tooltip: TT_DATE_PUBLISHED,
        center: false,
        enableSort: true,
    },
    {
        title: "Manufacturer",
        id: fieldMapper.manufacturer[dataset],
        tooltip: TT_MANUFACTURER,
        center: false,
        enableSort: true,
    },
    {
        title: "Model Number",
        id: fieldMapper.modelNumber[dataset],
        tooltip: TT_MODEL,
        center: false,
        enableSort: true,
    },
    {
        title: "Model Ref",
        id: fieldMapper.modelRef[dataset],
        tooltip: TT_MODEL,
        center: false,
        enableSort: true,
    },
    {
        title: "Type",
        id: "",
        tooltip: TT_TYPE,
        center: false,
        enableSort: false,
    },
    {
        title: "Phase",
        id: fieldMapper.phases[dataset],
        tooltip: TT_PHASE,
        center: true,
        enableSort: true,
    },
    {
        title: "Max Demand (A)",
        id: fieldMapper.maxDemand[dataset],
        tooltip: TT_MAX_DEMAND,
        center: true,
        enableSort: true,
    },
    {
        title: "Voltage (V)",
        id: fieldMapper.voltage[dataset],
        tooltip: TT_VOLTAGE,
        center: true,
        enableSort: true,
    },
]

interface DatabaseSearchTableProps {
    searchResults: IDemDatabaseSearchResult[] | undefined
    orderBy: string
    orderDirection: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    isLoading: boolean
    isPopupMode?: boolean
    onDeviceSelect?: (device: IDemDatabaseSearchResult) => void
}

/**
 * Default props for DemandSearchResults component
 */
const defaultProps = {
    isPopupMode: false,
    onDeviceSelect: undefined,
} as const

/**
 * Displays database search results table for demand devices
 * @example
 * <DemandSearchResults searchResults={searchResults}/>
 */
const DemandSearchResults = ({
    searchResults,
    orderBy,
    orderDirection,
    setOrderBy,
    isLoading,
    isPopupMode = defaultProps.isPopupMode,
    onDeviceSelect = defaultProps.onDeviceSelect,
}: DatabaseSearchTableProps): ReactElement => {
    const navigate = useNavigate()
    const { dataset: routeDataset } = useParams()
    const [searchParams] = useSearchParams()

    const queryDataset = searchParams.get("dataset")
    const datasetCandidate = queryDataset || routeDataset
    const dataset =
        datasetCandidate &&
        Object.values(LCTOnlyDatasets).includes(
            datasetCandidate as LCTOnlyDatasets
        )
            ? (datasetCandidate as LCTOnlyDatasets)
            : LCTOnlyDatasets.HP
    const handleRequestSort = (
        event_: MouseEvent<unknown>,
        property: keyof IDemDatabaseSearchResult
    ): void => {
        const isAsc = orderBy === property && orderDirection === ASC
        setOrderBy(property, isAsc ? DESC : ASC)
    }
    const createSortHandler =
        (property: keyof IDemDatabaseSearchResult) =>
        (event: MouseEvent<unknown>) => {
            handleRequestSort(event, property)
        }

    const isDraftStatus = (status: string): boolean =>
        status === DRAFT_AMENDMENT || status === DRAFT_DEVICE
    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(searchResults)) {
            return searchResults.length === 0
        }
        if (!searchResults) {
            return true
        }
        return false
    }

    // popup mode action column
    const renderActionColumn = (
        device: IDemDatabaseSearchResult
    ): ReactElement | null => {
        if (!isPopupMode || !onDeviceSelect) return null

        return (
            <TableCell align="right">
                <Button
                    size="small"
                    color="primary"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={(e) => {
                        e.stopPropagation()
                        onDeviceSelect(device)
                    }}
                >
                    Select
                </Button>
            </TableCell>
        )
    }

    return !emptySearch() ? (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headings(dataset).map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.tooltip}
                                key={heading.title}
                            >
                                <AppTableCell
                                    sx={{
                                        paddingTop: 0,
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                    sortDirection={
                                        orderBy === heading.id
                                            ? (orderDirection as SortDirection)
                                            : false
                                    }
                                    align={
                                        heading.center ? "center" : undefined
                                    }
                                >
                                    <TableSortLabel
                                        active={orderBy === heading.id}
                                        direction={
                                            orderBy === heading.id
                                                ? (orderDirection as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                                : (ASC as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                        }
                                        onClick={createSortHandler(
                                            heading.id as keyof IDemDatabaseSearchResult
                                        )}
                                        disabled={!heading.enableSort}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                        {orderBy === heading.id ? (
                                            <Box
                                                component="span"
                                                sx={visuallyHidden}
                                            >
                                                {orderDirection === DESC
                                                    ? "sorted descending"
                                                    : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                        {isPopupMode && (
                            <TableCell align="right">Add Device</TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(20).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`SearchTableSkeleton${v}`}
                              >
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                                  <AppTableCell>
                                      <AppSkeleton />
                                  </AppTableCell>
                              </AppTableContentRow>
                          ))
                        : (searchResults as IDemDatabaseSearchResult[]).map(
                              (result: IDemDatabaseSearchResult) => (
                                  <AppTableContentRow
                                      key={
                                          result.deviceReference +
                                          result.databaseId
                                      }
                                      hover
                                      onClick={
                                          isPopupMode
                                              ? undefined
                                              : () =>
                                                    isDraftStatus(result.status)
                                                        ? navigate(
                                                              SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK(
                                                                  dataset,
                                                                  result.deviceReference
                                                              )
                                                          )
                                                        : navigate(
                                                              SINGLE_DEMAND_DEVICE_NAV_LINK(
                                                                  dataset,
                                                                  result.deviceReference
                                                              )
                                                          )
                                      }
                                      sx={{
                                          cursor: isPopupMode
                                              ? "default"
                                              : "pointer",
                                      }}
                                  >
                                      <AppTableCell
                                          sx={{ wordBreak: "break-word" }}
                                      >
                                          <Stack direction="row" height="100%">
                                              {result.isThreadUnread && (
                                                  <AppTooltip title="There are unread comments on this device.">
                                                      <FiberManualRecordIcon
                                                          color="primary"
                                                          sx={{
                                                              fontSize: "20px",
                                                              alignSelf:
                                                                  "center",
                                                              paddingRight:
                                                                  "4px",
                                                          }}
                                                      />
                                                  </AppTooltip>
                                              )}
                                              <CopyTypography
                                                  text={result.deviceReference}
                                                  typographyProps={{
                                                      sx: {
                                                          wordBreak:
                                                              "break-word",
                                                      },
                                                  }}
                                              />
                                              {result.hasActiveAlert && (
                                                  <AlertFlag />
                                              )}
                                              {result.isSubscribed && (
                                                  <Box
                                                      style={{
                                                          paddingLeft: "8px",
                                                      }}
                                                  >
                                                      <AppTooltip title="You are subscribed to this device">
                                                          <NotificationsActiveIcon color="primary" />
                                                      </AppTooltip>
                                                  </Box>
                                              )}
                                          </Stack>
                                      </AppTableCell>
                                      <AppTableCell align="center">
                                          <StatusChip status={result.status} />
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.datePublished}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.manufacturer}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.modelNumber}
                                      </AppTableCell>
                                      <AppTableCell>
                                          {result.modelRef}
                                      </AppTableCell>
                                      <AppTableCell>{result.type}</AppTableCell>
                                      <AppTableCell align="center">
                                          {result.phases}
                                      </AppTableCell>
                                      <AppTableCell align="center">
                                          {result.maxDemand}
                                      </AppTableCell>
                                      <AppTableCell align="center">
                                          {result.voltage}
                                      </AppTableCell>
                                      {renderActionColumn(result)}
                                  </AppTableContentRow>
                              )
                          )}
                </TableBody>
            </Table>
        </TableContainer>
    ) : (
        <AppPaper>
            <ZeroSearchResultsMsg />
        </AppPaper>
    )
}

// Set defaultProps on the component
DemandSearchResults.defaultProps = defaultProps

export default memo(DemandSearchResults)
